import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import AdminTopNavbar from './components/AdminTopNavbar';
import { deleteBroadcastCardAdmin } from '../api/adminApi';
import '../styles/premium-theme-enhanced.css';

const BroadcastCardManagement = () => {
    const navigate = useNavigate();
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingCard, setEditingCard] = useState(null);
    const [stats, setStats] = useState({});
    const [filters, setFilters] = useState({
        date: '',
        isActive: '',
        isPublished: '',
        targetPlan: ''
    });
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCards: 0
    });

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        imageUrl: '',
        scheduleTime: '15:00',
        scheduledDate: '',
        targetPlan: 'free',
        isActive: true
    });

    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, cardId: null });
    const [deletingId, setDeletingId] = useState(null);

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || (typeof window !== 'undefined' ? window._API_BASE : undefined) || 'http://localhost:5000';

    useEffect(() => {
        fetchCards();
        fetchStats();

        // Auto-refresh every 30 seconds to update status (e.g. when scheduler publishes cards)
        const intervalId = setInterval(() => {
            fetchCards();
            fetchStats();
        }, 30000);

        return () => clearInterval(intervalId);
    }, [filters, pagination.currentPage]);

    const getAuthHeaders = () => {
        const token = localStorage.getItem('adminToken');
        return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        };
    };

    const fetchCards = async () => {
        try {
            setLoading(true);
            const queryParams = new URLSearchParams({
                page: pagination.currentPage,
                limit: 10,
                ...filters
            });

            const response = await axios.get(
                `${API_BASE_URL}/auth/admin/broadcast-cards?${queryParams}`,
                { headers: getAuthHeaders() }
            );

            if (response.data.success) {
                setCards(response.data.cards);
                setPagination(response.data.pagination);
            } else {
                toast.error(response.data.error || 'Failed to fetch broadcast cards');
            }
        } catch (error) {
            console.error('Error fetching cards:', error);
            toast.error('Failed to fetch broadcast cards');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateTestPublished = async () => {
        try {
            setLoading(true);
            const response = await axios.post(
                `${API_BASE_URL}/auth/admin/broadcast-cards/create-test-published`,
                {},
                { headers: getAuthHeaders() }
            );
            if (response.data.success) {
                toast.success('Test published card created');
                await fetchCards();
                await fetchStats();
            } else {
                toast.error(response.data.error || 'Failed to create test card');
            }
        } catch (e) {
            console.error('Error creating test published card:', e);
            toast.error('Failed to create test published card');
        } finally {
            setLoading(false);
        }
    };

    const handlePublishAll = async () => {
        try {
            setLoading(true);
            const body = {};
            if (filters.date) body.date = filters.date; // publish for selected date, else defaults to today on backend
            const response = await axios.post(
                `${API_BASE_URL}/auth/admin/broadcast-cards/publish-all`,
                body,
                { headers: getAuthHeaders() }
            );

            if (response.data.success) {
                toast.success(response.data.message || 'Published all pending broadcast cards');
                await fetchCards();
                await fetchStats();
                // Navigate to admin landing page
                navigate('/admin/dashboard');
            } else {
                toast.error(response.data.error || 'Failed to bulk publish');
            }
        } catch (error) {
            console.error('Error bulk publishing cards:', error);
            toast.error('Failed to publish all broadcast cards');
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/auth/admin/broadcast-cards-stats`,
                { headers: getAuthHeaders() }
            );

            if (response.data.success) {
                setStats(response.data.stats);
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    const handleImageUpload = async (file) => {
        try {
            const formData = new FormData();
            formData.append('image', file);

            const response = await axios.post(
                `${API_BASE_URL}/auth/admin/upload-broadcast-card-image`,
                formData,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            if (response.data.status === 'success') {
                return response.data.imageUrl;
            } else {
                throw new Error(response.data.error || 'Upload failed');
            }
        } catch (error) {
            console.error('Error uploading image:', error);
            toast.error('Failed to upload image');
            throw error;
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);

            let imageUrl = formData.imageUrl;

            // Upload image if a new file is selected
            if (imageFile) {
                imageUrl = await handleImageUpload(imageFile);
            }

            // Backend requires description; since UI removed it, fallback to title if empty
            const description = (formData.description || '').trim() || formData.title;
            const cardData = {
                ...formData,
                description,
                imageUrl
            };

            let response;
            if (editingCard) {
                response = await axios.put(
                    `${API_BASE_URL}/auth/admin/broadcast-cards/${editingCard._id}`,
                    cardData,
                    { headers: getAuthHeaders() }
                );
            } else {
                response = await axios.post(
                    `${API_BASE_URL}/auth/admin/broadcast-cards`,
                    cardData,
                    { headers: getAuthHeaders() }
                );
            }

            if (response.data.success) {
                toast.success(response.data.message);
                setShowModal(false);
                resetForm();
                fetchCards();
                fetchStats();
            } else {
                toast.error(response.data.error || 'Operation failed');
            }
        } catch (error) {
            console.error('Error saving card:', error);
            toast.error('Failed to save broadcast card');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (card) => {
        setEditingCard(card);
        setFormData({
            title: card.title,
            // Pre-fill description silently (UI hidden); fallback to title if missing
            description: card.description || card.title,
            imageUrl: card.imageUrl,
            scheduleTime: card.scheduleTime,
            scheduledDate: new Date(card.scheduledDate).toISOString().split('T')[0],
            targetPlan: card.targetPlan,
            isActive: card.isActive
        });
        setImagePreview(card.imageUrl ? (card.imageUrl.startsWith('http') ? card.imageUrl : `${API_BASE_URL}${card.imageUrl}`) : '');
        setShowModal(true);
    };

    const handleDelete = (cardId) => {
        // Debounce multiple rapid clicks by ignoring if a confirm is already open or deletion in progress
        if (deleteConfirm.open || deletingId) return;
        setDeleteConfirm({ open: true, cardId });
    };

    const performDelete = async () => {
        const cardId = deleteConfirm.cardId;
        if (!cardId) return;
        try {
            setDeletingId(cardId);
            const responseData = await deleteBroadcastCardAdmin(cardId);
            if (responseData.success) {
                toast.success(responseData.message);
                setDeleteConfirm({ open: false, cardId: null });
                fetchCards();
                fetchStats();
            } else {
                toast.error(responseData.error || 'Failed to delete card');
            }
        } catch (error) {
            console.error('Error deleting card:', error);
            const backendMsg = error?.error || error?.message || error?.response?.data?.error || error?.response?.data?.message;
            const status = error?.status || error?.response?.status;
            toast.error(backendMsg ? `Delete failed${status ? ` (${status})` : ''}: ${backendMsg}` : 'Failed to delete broadcast card');
        } finally {
            setDeletingId(null);
        }
    };

    const handlePublishNow = async (cardId) => {
        try {
            const response = await axios.post(
                `${API_BASE_URL}/auth/admin/broadcast-cards/${cardId}/publish-now`,
                {},
                { headers: getAuthHeaders() }
            );

            if (response.data.success) {
                toast.success(response.data.message || 'Broadcast card published');
                fetchCards();
                fetchStats();
            } else {
                toast.error(response.data.error || 'Failed to publish broadcast card');
            }
        } catch (error) {
            console.error('Error publishing card now:', error);
            toast.error('Failed to publish broadcast card');
        }
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            imageUrl: '',
            scheduleTime: '15:00',
            scheduledDate: '',
            targetPlan: 'free',
            isActive: true
        });
        setEditingCard(null);
        setImageFile(null);
        setImagePreview('');
    };

    const formatDateTime = (date, time) => {
        const dateObj = new Date(date);
        const [hours, minutes] = time.split(':');
        dateObj.setHours(parseInt(hours), parseInt(minutes));
        return dateObj.toLocaleString();
    };

    return (
        <div className="min-h-screen bg-gray-900">
            <AdminTopNavbar />

            <div className="p-6">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-2">
                        <h1 className="text-3xl font-bold text-blue-400">Broadcast Card Management</h1>
                        <button
                            onClick={() => navigate(-1)}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700/50 backdrop-blur-sm transition-colors duration-200"
                            aria-label="Go back"
                        >
                            <i className="bi bi-arrow-left" />
                            Back
                        </button>
                    </div>
                    <p className="text-gray-400">Manage daily challenges for streamers</p>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <div className="bg-gray-800/30 backdrop-blur-md p-6 rounded-xl shadow-2xl border border-gray-700/50">
                        <h3 className="text-sm font-medium text-gray-400">Total Cards</h3>
                        <p className="text-2xl font-bold text-white">{stats.totalCards || 0}</p>
                    </div>
                    <div className="bg-gray-800/30 backdrop-blur-md p-6 rounded-xl shadow-2xl border border-gray-700/50">
                        <h3 className="text-sm font-medium text-gray-400">Published Cards</h3>
                        <p className="text-2xl font-bold text-green-400">{stats.publishedCards || 0}</p>
                    </div>
                    <div className="bg-gray-800/30 backdrop-blur-md p-6 rounded-xl shadow-2xl border border-gray-700/50">
                        <h3 className="text-sm font-medium text-gray-400">Total Views</h3>
                        <p className="text-2xl font-bold text-blue-400">{stats.totalViews || 0}</p>
                    </div>
                    <div className="bg-gray-800/30 backdrop-blur-md p-6 rounded-xl shadow-2xl border border-gray-700/50">
                        <h3 className="text-sm font-medium text-gray-400">Total Engagements</h3>
                        <p className="text-2xl font-bold text-red-400">{stats.totalEngagements || 0}</p>
                    </div>
                </div>

                {/* Filters and Actions */}
                <div className="bg-gray-800/30 backdrop-blur-md p-6 rounded-xl shadow-2xl border border-gray-700/50 mb-6">
                    <div className="flex flex-wrap gap-4 items-center justify-between">
                        <div className="flex flex-wrap gap-4">
                            <div>
                                <input
                                    type="date"
                                    value={filters.date}
                                    onChange={(e) => setFilters({ ...filters, date: e.target.value })}
                                    className="px-3 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent backdrop-blur-sm w-56 sm:w-64 md:w-72"
                                    style={{ colorScheme: 'dark' }}
                                />
                            </div>
                            <select
                                value={filters.isActive}
                                onChange={(e) => setFilters({ ...filters, isActive: e.target.value })}
                                className="px-3 py-2 pr-10 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent backdrop-blur-sm w-56 sm:w-64 md:w-72"
                            >
                                <option value="">All Status</option>
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                            </select>
                            <select
                                value={filters.targetPlan}
                                onChange={(e) => setFilters({ ...filters, targetPlan: e.target.value })}
                                className="px-3 py-2 pr-10 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent backdrop-blur-sm w-56 sm:w-64 md:w-72"
                            >
                                <option value="">All Plans</option>
                                <option value="free">Free</option>
                                <option value="premium">Premium</option>
                                <option value="all">All Plans</option>
                            </select>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => {
                                    resetForm();
                                    setShowModal(true);
                                }}
                                className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-blue-500/25"
                            >
                                Add New Card
                            </button>
                        </div>
                    </div>
                </div>

                {/* Cards Table */}
                <div className="bg-gray-800/30 backdrop-blur-md rounded-xl shadow-2xl border border-gray-700/50 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-700/50">
                            <thead className="bg-gray-800/50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-red-300 uppercase tracking-wider">
                                        Challenge
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-red-300 uppercase tracking-wider">
                                        Schedule
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-red-300 uppercase tracking-wider">
                                        Target Plan
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-red-300 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-red-300 uppercase tracking-wider">
                                        Engagement
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-red-300 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-gray-800/20 divide-y divide-gray-700/30">
                                {loading ? (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-4 text-center">
                                            <div className="flex justify-center">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : cards.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-4 text-center text-gray-400">
                                            No broadcast cards found
                                        </td>
                                    </tr>
                                ) : (
                                    cards.map((card) => (
                                        <tr key={card._id} className="hover:bg-gray-700/30 transition-colors duration-200">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center">
                                                    {card.imageUrl && (
                                                        <img
                                                            src={card.imageUrl.startsWith('http') ? card.imageUrl : `${API_BASE_URL}${card.imageUrl}`}
                                                            alt={card.title}
                                                            className="h-12 w-12 rounded-lg object-cover mr-4 border border-gray-600"
                                                        />
                                                    )}
                                                    <div>
                                                        <div className="text-sm font-medium text-white">
                                                            {card.title}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-300">
                                                    {formatDateTime(card.scheduledDate, card.scheduleTime)}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${card.targetPlan === 'free' ? 'bg-green-500/20 text-green-300 border-green-500/30' :
                                                    card.targetPlan === 'premium' ? 'bg-red-500/20 text-red-300 border-red-500/30' :
                                                        'bg-blue-500/20 text-blue-300 border-blue-500/30'
                                                    }`}>
                                                    {card.targetPlan}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex flex-col">
                                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full mb-1 border ${card.isActive ? 'bg-green-500/20 text-green-300 border-green-500/30' : 'bg-red-500/20 text-red-300 border-red-500/30'
                                                        }`}>
                                                        {card.isActive ? 'Active' : 'Inactive'}
                                                    </span>
                                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${card.isPublished ? 'bg-blue-500/20 text-blue-300 border-blue-500/30' : 'bg-gray-500/20 text-gray-300 border-gray-500/30'
                                                        }`}>
                                                        {card.isPublished ? 'Published' : 'Pending'}
                                                    </span>
                                                    {card.isPublished && (
                                                        <div className="mt-1 text-[11px] text-gray-400 leading-tight">
                                                            <div>PublishedAt: {card.publishedAt ? new Date(card.publishedAt).toLocaleTimeString() : '-'}</div>
                                                            <div>ExpiresAt: {card.expiresAt ? new Date(card.expiresAt).toLocaleTimeString() : '-'}</div>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                                                <div>Views: {card.viewCount || 0}</div>
                                                <div>Engagements: {card.engagementCount || 0}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                {!card.isPublished && (
                                                    <button
                                                        onClick={() => handlePublishNow(card._id)}
                                                        className="text-green-400 hover:text-green-300 mr-4 transition-colors duration-200"
                                                    >
                                                        Publish Now
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => handleEdit(card)}
                                                    className="text-red-400 hover:text-red-300 mr-4 transition-colors duration-200"
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(card._id)}
                                                    disabled={deletingId === card._id}
                                                    className={`text-blue-400 hover:text-blue-300 transition-colors duration-200 ${deletingId === card._id ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                >
                                                    {deletingId === card._id ? 'Deleting…' : 'Delete'}
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                        <div className="bg-gray-800/30 px-4 py-3 flex items-center justify-between border-t border-gray-700/50">
                            <div className="flex-1 flex justify-between sm:hidden">
                                <button
                                    onClick={() => setPagination({ ...pagination, currentPage: pagination.currentPage - 1 })}
                                    disabled={!pagination.hasPrev}
                                    className="relative inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-lg text-gray-300 bg-gray-700/50 hover:bg-gray-600/50 disabled:opacity-50 backdrop-blur-sm"
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => setPagination({ ...pagination, currentPage: pagination.currentPage + 1 })}
                                    disabled={!pagination.hasNext}
                                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-lg text-gray-300 bg-gray-700/50 hover:bg-gray-600/50 disabled:opacity-50 backdrop-blur-sm"
                                >
                                    Next
                                </button>
                            </div>
                            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                <div>
                                    <p className="text-sm text-gray-300">
                                        Showing page <span className="font-medium">{pagination.currentPage}</span> of{' '}
                                        <span className="font-medium">{pagination.totalPages}</span>
                                    </p>
                                </div>
                                <div>
                                    <nav className="relative z-0 inline-flex rounded-lg shadow-sm -space-x-px">
                                        <button
                                            onClick={() => setPagination({ ...pagination, currentPage: pagination.currentPage - 1 })}
                                            disabled={!pagination.hasPrev}
                                            className="relative inline-flex items-center px-2 py-2 rounded-l-lg border border-gray-600 bg-gray-700/50 text-sm font-medium text-gray-300 hover:bg-gray-600/50 disabled:opacity-50 backdrop-blur-sm"
                                        >
                                            Previous
                                        </button>
                                        <button
                                            onClick={() => setPagination({ ...pagination, currentPage: pagination.currentPage + 1 })}
                                            disabled={!pagination.hasNext}
                                            className="relative inline-flex items-center px-2 py-2 rounded-r-lg border border-gray-600 bg-gray-700/50 text-sm font-medium text-gray-300 hover:bg-gray-600/50 disabled:opacity-50 backdrop-blur-sm"
                                        >
                                            Next
                                        </button>
                                    </nav>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Create/Edit Modal */}
                {showModal && (
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
                        <div className="relative top-20 mx-auto p-5 border border-gray-700/50 w-11/12 md:w-3/4 lg:w-1/2 shadow-2xl rounded-xl bg-gray-800/90 backdrop-blur-md">
                            <div className="mt-3">
                                <h3 className="text-lg font-medium text-white mb-4">
                                    {editingCard ? 'Edit Broadcast Card' : 'Create New Broadcast Card'}
                                </h3>
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-blue-300 mb-1">
                                            Challenge Title
                                        </label>
                                        <select
                                            value={formData.title}
                                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                            required
                                            className="w-full px-3 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent backdrop-blur-sm"
                                        >
                                            <option value="">Select challenge</option>
                                            <option value="Warriors">Warriors</option>
                                            <option value="Gladiators">Gladiators</option>
                                            <option value="Conquerors">Conquerors</option>
                                            <option value="Titans">Titans</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-blue-300 mb-1">
                                            Challenge Image
                                        </label>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageChange}
                                            className="w-full px-3 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent backdrop-blur-sm"
                                        />
                                        {imagePreview && (
                                            <div className="mt-2">
                                                <img
                                                    src={imagePreview}
                                                    alt="Preview"
                                                    className="h-32 w-32 object-cover rounded-lg border border-gray-600"
                                                />
                                            </div>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-blue-300 mb-1">
                                                Schedule Date
                                            </label>
                                            <input
                                                type="date"
                                                value={formData.scheduledDate}
                                                onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })}
                                                required
                                                min={new Date().toISOString().split('T')[0]}
                                                className="w-full px-3 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent backdrop-blur-sm"
                                                style={{ colorScheme: 'dark' }}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-blue-300 mb-1">
                                                Schedule Time
                                            </label>
                                            <input
                                                type="time"
                                                value={formData.scheduleTime}
                                                onChange={(e) => setFormData({ ...formData, scheduleTime: e.target.value })}
                                                required
                                                className="w-full px-3 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent backdrop-blur-sm"
                                                style={{ colorScheme: 'dark' }}
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-blue-300 mb-1">
                                                Target Plan
                                            </label>
                                            <select
                                                value={formData.targetPlan}
                                                onChange={(e) => setFormData({ ...formData, targetPlan: e.target.value })}
                                                className="w-full px-3 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent backdrop-blur-sm"
                                            >
                                                <option value="free">Free Plan Only</option>
                                                <option value="premium">Premium Plan Only</option>
                                                <option value="all">All Plans</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-blue-300 mb-1">
                                                Status
                                            </label>
                                            <select
                                                value={formData.isActive}
                                                onChange={(e) => setFormData({ ...formData, isActive: e.target.value === 'true' })}
                                                className="w-full px-3 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent backdrop-blur-sm"
                                            >
                                                <option value="true">Active</option>
                                                <option value="false">Inactive</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="flex justify-end space-x-3 pt-4">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowModal(false);
                                                resetForm();
                                            }}
                                            className="px-4 py-2 border border-gray-600 rounded-lg text-gray-300 hover:bg-gray-700/50 focus:outline-none focus:ring-2 focus:ring-red-500 backdrop-blur-sm transition-colors duration-200"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-blue-500/25"
                                        >
                                            {loading ? 'Saving...' : (editingCard ? 'Update Card' : 'Create Card')}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {deleteConfirm.open && (
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
                        <div className="bg-gray-800/90 backdrop-blur-md rounded-xl shadow-2xl w-11/12 md:w-1/3 p-6 border border-gray-700/50">
                            <h4 className="text-lg font-semibold text-white mb-2">Confirm Deletion</h4>
                            <p className="text-gray-300 mb-6">Are you sure you want to delete this broadcast card? This action cannot be undone.</p>
                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={() => setDeleteConfirm({ open: false, cardId: null })}
                                    disabled={!!deletingId}
                                    className="px-4 py-2 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700/50 disabled:opacity-50 backdrop-blur-sm transition-colors duration-200"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={performDelete}
                                    disabled={!!deletingId}
                                    className="px-4 py-2 rounded-lg bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-700 hover:to-red-800 disabled:opacity-50 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-red-500/25"
                                >
                                    {deletingId ? 'Deleting…' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default BroadcastCardManagement;
